import type { ReactNode } from 'react';

import { cx } from './cx';
import { Icon, type IconName } from './Icon';

/**
 * A tinted strip with an icon: what just happened, or what is about to.
 *
 * `footer` is the form that sits at the bottom of a card with a rule above it — the
 * confirmation of the last order taken lives there, so a dispatcher looking at the phone
 * rather than the screen still finds the reference where they left it.
 *
 * `stack` puts the action under the message instead of beside it. Beside is right for a
 * word or two — Dismiss, Undo — and wrong as soon as the message is a sentence: the text
 * wraps into a narrow column while the button sits in the space it was squeezed out of,
 * and a button that has to be hovered to look like one is not a button. A card three
 * hundred pixels wide reaches that point almost immediately.
 */
export type BannerTone = 'ok' | 'warn' | 'danger';

const LOOK: Record<BannerTone, string> = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
};

const ICON: Record<BannerTone, IconName> = { ok: 'check', warn: 'alert', danger: 'alert' };

export function Banner({
  tone = 'ok',
  footer,
  icon = true,
  actions,
  stack,
  className,
  children,
}: {
  tone?: BannerTone;
  footer?: boolean;
  icon?: boolean;
  actions?: ReactNode;
  /** Put the action under the message rather than beside it. */
  stack?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const ground = cx(
    'px-3.5 text-[13px]',
    // A row is a line of text and sits tight. Stacked, the last thing in the strip is a
    // button, and 8px under it reads as cramped beside the order form alongside — whose
    // body is `pt-3 pb-3.5`, so its Accept button has 14px beneath it. Matching that is
    // what stops two buttons on one screen looking like two different systems.
    stack ? 'pt-2.5 pb-3.5' : 'py-2',
    footer ? 'border-t border-rule' : 'rounded-control',
    LOOK[tone],
    className,
  );

  const role = tone === 'danger' ? 'alert' : 'status';

  // Two shapes rather than one parameterised shape, because the row is on every other
  // banner in the console and must not move while this one is being added.
  if (stack) {
    return (
      <div role={role} className={cx('flex flex-col gap-2', ground)}>
        <div className="flex items-start gap-1.5">
          {icon ? <Icon name={ICON[tone]} small /> : null}
          <span className="min-w-0 flex-1">{children}</span>
        </div>
        {/* Trailing, where the eye finishes the sentence and where every other confirming
            action in the console sits. Wrapped rather than aligned on the child, so more
            than one action still lines up. */}
        {actions ? <div className="flex justify-end gap-1.5">{actions}</div> : null}
      </div>
    );
  }

  return (
    <div role={role} className={cx('flex items-center gap-1.5', ground)}>
      {icon ? <Icon name={ICON[tone]} small /> : null}
      <span className="min-w-0 flex-1">{children}</span>
      {actions}
    </div>
  );
}
